import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Context } from "@/lib/Context";
import { priceDiffPercentage, sendWsMessage } from "@/lib/utils";
import { Instrument } from "@/models/instruments";

export type TickerEntry = [string, Instrument];

export interface TickerSnapshot {
    data: Instrument;
    isLoading: boolean;
}

export interface TickerFeedStore {
    getSnapshot: (key: string) => TickerSnapshot;
    subscribe: (key: string, listener: () => void) => () => void;
}

// How often buffered live updates are emitted to subscribers. WS frames are
// coalesced into a pending set and flushed on this cadence so a fast feed
// doesn't re-render every item on every frame (important on mobile).
const FLUSH_MS = 400;
const PRICE_EPS = 0.01;
const INITIAL_LOAD_TIMEOUT_MS = 600;
const EMPTY_SNAPSHOT: TickerSnapshot = { data: {} as Instrument, isLoading: true };

/**
 * Centralized WebSocket feed for the ticker tape, exposed as an external store.
 *
 * Two problems shaped this design:
 *  1. Perf: previously every `TickerStock` (× react-ticker's chain-mode copies)
 *     attached its own `message` listener and `JSON.parse`-d every frame. Here a
 *     single listener per socket parses each frame once and routes it to the
 *     matching instrument via a precomputed identifier index.
 *  2. react-ticker snapshots its children at construction and never re-renders
 *     them from new props, so the leaves can't receive live data through props.
 *     Instead each leaf subscribes to this store by key and re-renders itself in
 *     place when its own data changes (see `useTickerInstrument`).
 */
export const useTickerTapeFeed = (instrumentsList: TickerEntry[]): TickerFeedStore => {
    const { multiWebSocket, ortexWebSocket } = useContext(Context);

    const baseRef = useRef<Map<string, Instrument>>(new Map());
    const overridesRef = useRef<Map<string, Partial<Instrument>>>(new Map());
    const snapshotsRef = useRef<Map<string, TickerSnapshot>>(new Map());
    const listenersRef = useRef<Map<string, Set<() => void>>>(new Map());
    const loadedRef = useRef<Set<string>>(new Set());
    const subscribedRef = useRef<Set<string>>(new Set());
    const pendingRef = useRef<Set<string>>(new Set());
    const loadingExpiredRef = useRef(false);

    // Recompute and cache a key's snapshot (new identity only when it changes,
    // so subscribers' equality bail-out works).
    const recompute = (key: string) => {
        const data = { ...baseRef.current.get(key), ...overridesRef.current.get(key) } as Instrument;
        const isLoading = !loadingExpiredRef.current && !loadedRef.current.has(key);
        snapshotsRef.current.set(key, { data, isLoading });
    };

    const emit = (key: string) => listenersRef.current.get(key)?.forEach((cb) => cb());

    // Stable store handle handed to every leaf (survives react-ticker freezing
    // the element tree because the reference never changes).
    const storeRef = useRef<TickerFeedStore>();
    if (!storeRef.current) {
        storeRef.current = {
            getSnapshot: (key) => snapshotsRef.current.get(key) ?? EMPTY_SNAPSHOT,
            subscribe: (key, listener) => {
                let set = listenersRef.current.get(key);
                if (!set) {
                    set = new Set();
                    listenersRef.current.set(key, set);
                }
                set.add(listener);
                return () => {
                    set!.delete(listener);
                };
            },
        };
    }

    // Identifier indexes for routing incoming messages to an instrument key.
    // Also seeds base data synchronously so the very first render already has
    // content (react-ticker measures element width at construction; an empty
    // leaf would measure as zero and break the chain layout).
    const { multiIndex, ortexIndex } = useMemo(() => {
        const multiIndex = new Map<string, string>(); // incoming ISIN -> key
        const ortexIndex = new Map<string, string>(); // incoming identifier -> key

        for (const [key, data] of instrumentsList) {
            baseRef.current.set(key, data);
            recompute(key);

            if (data.exchange) {
                ortexIndex.set(key, key);
                if (data.ticker) ortexIndex.set(data.ticker, key);
                if (data.isin) {
                    if (data.currency === "GBP") {
                        ortexIndex.set(`${data.isin}GBP`, key);
                        ortexIndex.set(`${data.isin}GBX`, key);
                    } else {
                        ortexIndex.set(`${data.isin}${data.currency}`, key);
                    }
                }
            } else {
                multiIndex.set(key, key);
            }
        }
        return { multiIndex, ortexIndex };
    }, [instrumentsList]);

    const multiIndexRef = useRef(multiIndex);
    const ortexIndexRef = useRef(ortexIndex);
    useEffect(() => {
        multiIndexRef.current = multiIndex;
        ortexIndexRef.current = ortexIndex;
    }, [multiIndex, ortexIndex]);

    // Seed/refresh base data from the instrument list and notify subscribers.
    useEffect(() => {
        const presentKeys = new Set<string>();
        for (const [key, data] of instrumentsList) {
            presentKeys.add(key);
            baseRef.current.set(key, data);
            recompute(key);
            emit(key);
        }
        // Forget instruments no longer in the list.
        Array.from(baseRef.current.keys()).forEach((key) => {
            if (!presentKeys.has(key)) {
                baseRef.current.delete(key);
                overridesRef.current.delete(key);
                snapshotsRef.current.delete(key);
                loadedRef.current.delete(key);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instrumentsList]);

    // Flush buffered updates on a fixed cadence.
    useEffect(() => {
        const id = setInterval(() => {
            if (pendingRef.current.size === 0) return;
            const keys = Array.from(pendingRef.current);
            pendingRef.current.clear();
            keys.forEach((key) => {
                recompute(key);
                emit(key);
            });
        }, FLUSH_MS);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Clear all spinners after the initial grace period.
    useEffect(() => {
        const t = setTimeout(() => {
            loadingExpiredRef.current = true;
            Array.from(snapshotsRef.current.keys()).forEach((key) => {
                recompute(key);
                emit(key);
            });
        }, INITIAL_LOAD_TIMEOUT_MS);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Subscribe each instrument exactly once (re-subscribing on socket replace).
    const lastMultiRef = useRef(multiWebSocket);
    const lastOrtexRef = useRef(ortexWebSocket);
    useEffect(() => {
        if (!multiWebSocket || !ortexWebSocket) return;

        if (lastMultiRef.current !== multiWebSocket || lastOrtexRef.current !== ortexWebSocket) {
            subscribedRef.current = new Set();
            lastMultiRef.current = multiWebSocket;
            lastOrtexRef.current = ortexWebSocket;
        }

        const presentKeys = new Set(instrumentsList.map(([key]) => key));
        Array.from(subscribedRef.current).forEach((key) => {
            if (!presentKeys.has(key)) subscribedRef.current.delete(key);
        });

        for (const [key, data] of instrumentsList) {
            if (subscribedRef.current.has(key)) continue;
            subscribedRef.current.add(key);

            const entry: TickerEntry = [key, data];
            const useOrtex = !!data.exchange;
            const socket = useOrtex ? ortexWebSocket : multiWebSocket;
            const send = () => sendWsMessage(entry, multiWebSocket, ortexWebSocket, useOrtex);

            if (socket.readyState === WebSocket.OPEN) {
                send();
            } else {
                socket.addEventListener("open", send, { once: true });
            }
        }
    }, [instrumentsList, multiWebSocket, ortexWebSocket]);

    // Buffer a live update for the next flush.
    const applyUpdate = (key: string, patch: Partial<Instrument>) => {
        overridesRef.current.set(key, { ...overridesRef.current.get(key), ...patch });
        loadedRef.current.add(key);
        pendingRef.current.add(key);
    };

    const current = (key: string): Instrument => snapshotsRef.current.get(key)?.data || ({ ...baseRef.current.get(key) } as Instrument);

    // Single listener per socket: parse once, route to the matching instrument.
    useEffect(() => {
        if (!multiWebSocket || !ortexWebSocket) return;

        const onMulti = ({ data }: MessageEvent) => {
            let parsed: any;
            try {
                parsed = JSON.parse(data);
            } catch {
                return;
            }
            const ISIN = parsed?.ISIN;
            if (ISIN == null) return;
            const key = multiIndexRef.current.get(ISIN);
            if (!key) return;

            const cur = current(key);
            const T = parsed?.T;
            const tsLast = parsed?.["ts-last"];
            const update = parsed?.update;

            const next: Partial<Instrument> = T
                ? {
                      price: T?.C?.c ?? cur.price,
                      lastChange: (T?.C?.c || 0) - (cur.lastPrice || 0),
                  }
                : {
                      price: tsLast?.[1] ?? cur.price,
                      change: update?.nch ?? cur.change,
                      percentage: update?.pch ?? cur.percentage,
                      timestamp: tsLast?.[0] ?? cur.timestamp,
                  };

            const priceDiff = Math.abs((cur.price ?? 0) - (next.price ?? 0)) > PRICE_EPS;
            const percentageDiff = Math.abs((cur.percentage ?? 0) - (next.percentage ?? cur.percentage ?? 0)) > PRICE_EPS;
            const timeDiff = (next.timestamp ?? cur.timestamp ?? 0) - (cur.timestamp ?? 0) > 60 * 1000;
            if (priceDiff || percentageDiff || timeDiff) {
                applyUpdate(key, next);
            }
        };

        const onOrtex = ({ data }: MessageEvent) => {
            let parsed: any;
            try {
                parsed = JSON.parse(data);
            } catch {
                return;
            }
            const { CT, T } = parsed;
            const LT: [{ o: number; h: number; l: number; c: number }, number][] = parsed.LT;
            const ticker: string = parsed.ticker;

            if (CT) {
                const key = ortexIndexRef.current.get(CT.i);
                if (!key) return;
                const cur = current(key);
                if (!(Math.abs((cur.price ?? 0) - CT.P) > PRICE_EPS)) return;
                applyUpdate(key, { price: CT.P, change: CT.P - (cur.lastPrice ?? 0), percentage: priceDiffPercentage(cur.lastPrice, CT.P) });
            } else if (T) {
                const key = ortexIndexRef.current.get(T.i);
                if (!key) return;
                const cur = current(key);
                if (!(Math.abs((cur.price ?? 0) - T.C.c) > PRICE_EPS)) return;
                applyUpdate(key, { price: T.C.c, lastChange: T.C.c - (cur.lastPrice ?? 0) });
            } else if (Array.isArray(LT) && LT.length > 0) {
                const key = ortexIndexRef.current.get(ticker);
                if (!key) return;
                const last = LT.at(-1)?.[0];
                if (!last) return;
                const cur = current(key);
                applyUpdate(key, { price: last.c, lastChange: last.c - (cur.lastPrice ?? 0) });
            }
        };

        multiWebSocket.addEventListener("message", onMulti);
        ortexWebSocket.addEventListener("message", onOrtex);
        return () => {
            multiWebSocket.removeEventListener("message", onMulti);
            ortexWebSocket.removeEventListener("message", onOrtex);
        };
        // Handlers read live state from refs, so they only re-attach when the
        // socket instances themselves change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [multiWebSocket, ortexWebSocket]);

    return storeRef.current;
};

/**
 * Subscribe a leaf component to a single instrument's live snapshot. Re-renders
 * the component in place when that instrument changes, bypassing react-ticker's
 * frozen prop tree.
 */
export const useTickerInstrument = (store: TickerFeedStore, key: string): TickerSnapshot => {
    const [snapshot, setSnapshot] = useState<TickerSnapshot>(() => store.getSnapshot(key));

    useEffect(() => {
        const update = () => setSnapshot(store.getSnapshot(key));
        update(); // catch any change between render and effect attachment
        return store.subscribe(key, update);
    }, [store, key]);

    return snapshot;
};
